import { ChevronRight } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, H2, YStack } from 'tamagui';
import { Toggle, tokens } from 'tamagui-config';

import { MenuItem } from './components/MenuItem';
import { CustomDialog } from '../../components/common';
import { useFirmwareContext } from '../../contexts/FirmwareContext';
import { ROUTE_PATHNAMES } from '../../routes/routeConstants';
import { StyledCard } from '../Settings_General/components/styles';

enum LockScreenMenuItem {
  ENABLE_LOCK_SCREEN = 'enableLockScreen',
  SET_PIN = 'setPin',
  RESET_PIN = 'resetPin',
  LOCK_SCREEN = 'lockScreen',
}

enum DialogType {
  CHANGE_PIN = 'changePin',
  DISABLE_PIN = 'disablePin',
  LOCK_SCREEN = 'lockScreen',
}

export const LockScreenSettings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lock, isPinSet } = useFirmwareContext();

  const [dialogType, setDialogType] = useState<DialogType | null>(null);

  const onResetPin = () => {
    setDialogType(null);
    navigate(ROUTE_PATHNAMES.resetPin);
  };

  const handleDisablePin = () => {
    navigate(ROUTE_PATHNAMES.disablePin);
    setDialogType(null);
  };

  const onLockScreen = () => {
    lock();
    setDialogType(null);
  };

  const onTogglePin = (checked: boolean) => {
    if (!checked) {
      setDialogType(DialogType.DISABLE_PIN);
    }
  };

  const renderMenuItem = (itemType: LockScreenMenuItem) => {
    switch (itemType) {
      case LockScreenMenuItem.ENABLE_LOCK_SCREEN:
        return (
          <MenuItem
            rightElement={<Toggle checked={isPinSet} onCheckedChange={onTogglePin} />}
            label={t('settings.lockScreen.enableLockScreen')}
          />
        );

      case LockScreenMenuItem.SET_PIN:
        return (
          <MenuItem
            rightElement={<ChevronRight size={36} color="#FFFFFF3F" />}
            label={t('settings.lockScreen.setPin')}
            onPress={() => navigate(ROUTE_PATHNAMES.setPin)}
          />
        );

      case LockScreenMenuItem.RESET_PIN:
        return (
          <MenuItem
            rightElement={<ChevronRight size={36} color={tokens.color.white40} />}
            label={t('settings.lockScreen.resetPin')}
            onPress={() => setDialogType(DialogType.CHANGE_PIN)}
          />
        );

      case LockScreenMenuItem.LOCK_SCREEN:
        return (
          <MenuItem
            label={t('settings.lockScreen.lockScreen')}
            onPress={() => setDialogType(DialogType.LOCK_SCREEN)}
          />
        );
    }
  };

  const getDialogProps = (type: DialogType) => {
    switch (type) {
      case DialogType.CHANGE_PIN:
        return {
          buttonLabel: t('settings.lockScreen.changePin'),
          title: t('settings.lockScreen.changePin'),
          description: t('settings.lockScreen.changePinDescription'),
          onTryAgain: onResetPin,
        };

      case DialogType.DISABLE_PIN:
        return {
          buttonLabel: t('settings.lockScreen.disablePin'),
          title: t('settings.lockScreen.disablePin'),
          description: t('settings.lockScreen.disablePinDescription'),
          onTryAgain: handleDisablePin,
        };

      case DialogType.LOCK_SCREEN:
        return {
          buttonLabel: t('settings.lockScreen.lockScreen'),
          title: t('settings.lockScreen.lockScreen'),
          description: t('settings.lockScreen.lockScreenDescription'),
          onTryAgain: onLockScreen,
        };
    }
  };

  const renderDialog = (currentDialogType: DialogType, onCancelPress: () => void) => {
    const props = getDialogProps(currentDialogType);

    return (
      <CustomDialog
        width="301px"
        open={!!currentDialogType}
        onOpenChange={onCancelPress}
        onButtonPress={props.onTryAgain}
        buttonLabel={props.buttonLabel}
        title={props.title}
        description={props.description}>
        <Button
          onPress={onCancelPress}
          borderRadius={36}
          height="72px"
          backgroundColor={'transparent'}
          fontSize="$8.5"
          fontWeight={400}>
          {t('common.cancel')}
        </Button>
      </CustomDialog>
    );
  };

  return (
    <YStack padding="$5">
      <StyledCard height="432px">
        <H2 color="$white12" fontSize="$6" lineHeight="$5">
          {t('settings.lockScreen.title')}
        </H2>
        {isPinSet && renderMenuItem(LockScreenMenuItem.ENABLE_LOCK_SCREEN)}
        {!isPinSet && renderMenuItem(LockScreenMenuItem.SET_PIN)}
        {isPinSet && renderMenuItem(LockScreenMenuItem.RESET_PIN)}
        {renderMenuItem(LockScreenMenuItem.LOCK_SCREEN)}
      </StyledCard>
      {dialogType && renderDialog(dialogType, () => setDialogType(null))}
    </YStack>
  );
};
